import type { HttpClient } from '../http/HttpClient.ts';

export type SprintStatus = 'planned' | 'active' | 'completed';
export type SprintDestination = 'next_sprint' | 'backlog';

export interface Sprint {
  id: string;
  name: string;
  goal: string;
  startDate: string;
  endDate: string;
  status: SprintStatus;
  activatedAt: string | null;
  closedAt: string | null;
  closeTrigger: 'auto' | 'manual' | 'promote' | null;
  committedPoints: number | null;
  completedPoints: number | null;
  itemCount: number;
  doneCount: number;
  points: number;
  donePoints: number;
}

export interface SprintSettings {
  mode: 'scheduled' | 'manual';
  lengthDays: number;
  startWeekday: number;
  anchorDate: string | null;
  rollover: SprintDestination;
  autoCreateNext: boolean;
  timezone: string;
}

export interface UnfinishedItem {
  id: string;
  number: number;
  title: string;
  points: number | null;
}

export interface EndPreview {
  done: { items: number; points: number };
  unfinished: UnfinishedItem[];
  nextSprint: { id: string; name: string } | { new: string } | null;
}

export interface EndResult {
  sprint: Sprint;
  movedToSprint: number;
  movedToBacklog: number;
  nextSprint: { id: string; name: string } | null;
}

export interface EndOptions {
  destination: SprintDestination;
  perItem?: { itemId: string; to: SprintDestination }[];
}

export interface RescheduleChange {
  id: string;
  name: string;
  from: { start: string; end: string };
  to: { start: string; end: string };
}

export interface BurndownDay {
  date: string;
  totalPoints: number;
  donePoints: number;
  reviewPoints: number;
}

export interface Burndown {
  sprint: Pick<
    Sprint,
    'id' | 'name' | 'startDate' | 'endDate' | 'status' | 'committedPoints' | 'completedPoints'
  >;
  today: string;
  days: BurndownDay[];
  scope: { date: string; added: number; removed: number }[];
  totals: { added: number; removed: number; rolledIn: number; rolledOut: number };
}

export interface VelocityRow {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  committed: number;
  completed: number;
  review: number;
  rolledIn: number;
  rolledOut: number;
}

const base = (org: string, key: string) =>
  `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}`;

/** A project's sprints and sprint settings. */
export class SprintsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  list(org: string, key: string): Promise<{ sprints: Sprint[]; settings: SprintSettings }> {
    return this.#http.get(`${base(org, key)}/sprints`);
  }

  get(org: string, key: string, id: string): Promise<{ sprint: Sprint }> {
    return this.#http.get(`${base(org, key)}/sprints/${encodeURIComponent(id)}`);
  }

  create(
    org: string,
    key: string,
    input: { name?: string; goal?: string; startDate?: string; endDate?: string } = {},
  ): Promise<{ sprint: Sprint }> {
    return this.#http.post(`${base(org, key)}/sprints`, input);
  }

  update(
    org: string,
    key: string,
    id: string,
    patch: Partial<Pick<Sprint, 'name' | 'goal' | 'startDate' | 'endDate'>>,
  ): Promise<{ sprint: Sprint }> {
    return this.#http.patch(`${base(org, key)}/sprints/${encodeURIComponent(id)}`, patch);
  }

  delete(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/sprints/${encodeURIComponent(id)}`);
  }

  reorder(org: string, key: string, ids: string[]): Promise<{ sprints: Sprint[] }> {
    return this.#http.post(`${base(org, key)}/sprints/reorder`, { ids });
  }

  realign(org: string, key: string, dryRun: boolean): Promise<{ changes: RescheduleChange[] }> {
    return this.#http.post(`${base(org, key)}/sprints/realign`, { dryRun });
  }

  start(
    org: string,
    key: string,
    id: string,
    body: { ifActive?: 'swap' | 'end_current'; end?: EndOptions } = {},
  ): Promise<{ sprint: Sprint }> {
    return this.#http.post(`${base(org, key)}/sprints/${encodeURIComponent(id)}/start`, body);
  }

  previewEnd(org: string, key: string, id: string): Promise<EndPreview> {
    return this.#http.post(`${base(org, key)}/sprints/${encodeURIComponent(id)}/end`, {
      destination: 'backlog',
      dryRun: true,
    });
  }

  end(org: string, key: string, id: string, options: EndOptions): Promise<EndResult> {
    return this.#http.post(`${base(org, key)}/sprints/${encodeURIComponent(id)}/end`, options);
  }

  burndown(org: string, key: string, id: string): Promise<Burndown> {
    return this.#http.get(`${base(org, key)}/sprints/${encodeURIComponent(id)}/burndown`);
  }

  velocity(org: string, key: string, last: number): Promise<{ sprints: VelocityRow[] }> {
    return this.#http.get(`${base(org, key)}/velocity`, { query: { last: String(last) } });
  }

  settings(org: string, key: string): Promise<{ settings: SprintSettings }> {
    return this.#http.get(`${base(org, key)}/sprint-settings`);
  }

  saveSettings(
    org: string,
    key: string,
    patch: Partial<SprintSettings>,
  ): Promise<{ settings: SprintSettings }> {
    return this.#http.put(`${base(org, key)}/sprint-settings`, patch);
  }
}
